/**
 * TravelCategoryNav — persistent horizontal category strip shown on
 * every /travel/* page so users always know where they are and can
 * jump between airlines · airports · rail · bus · ferries · seaports ·
 * taxi · hotels · tours · car rentals with one click.
 */
import { Link, useLocation } from "react-router-dom";
import {
  Plane, Building2, Train, Bus, Ship, Anchor, Car, Hotel, Compass, MapPin,
} from "lucide-react";

const CATS = [
  { to: "/businesses?industry=hotel", label: "Hotels",         icon: Hotel,     match: /^\/businesses\?industry=hotel$/ },
  { to: "/travel/airlines",           label: "Airlines",       icon: Plane,     match: /^\/travel\/airlines/ },
  { to: "/travel/airports",           label: "Airports",       icon: Building2, match: /^\/travel\/airports/ },
  { to: "/travel/rail",               label: "Rail",           icon: Train,     match: /^\/travel\/rail(?!-stations)/ },
  { to: "/travel/rail-stations",      label: "Rail Stations",  icon: Train,     match: /^\/travel\/rail-stations/ },
  { to: "/travel/bus",                label: "Bus",            icon: Bus,       match: /^\/travel\/bus/ },
  { to: "/travel/cruises",            label: "Cruises",        icon: Ship,      match: /^\/travel\/cruises/ },
  { to: "/travel/seaports",           label: "Ports",          icon: Anchor,    match: /^\/travel\/seaports/ },
  { to: "/travel/taxi",               label: "Taxi",           icon: Car,       match: /^\/travel\/taxi/ },
  { to: "/travel/tours",              label: "Tours",          icon: Compass,   match: /^\/travel\/tours/, stub: true },
  { to: "/travel/rentals",            label: "Car Rentals",    icon: Car,       match: /^\/travel\/rentals/, stub: true },
];

export default function TravelCategoryNav() {
  const { pathname, search } = useLocation();
  const full = pathname + (search || "");
  return (
    <nav
      className="sticky top-0 z-30 -mx-4 md:mx-0 px-4 md:px-0 py-2 md:py-3 border-b overflow-x-auto"
      style={{
        background: "var(--wofy-bg)",
        borderColor: "var(--wofy-border)",
        backdropFilter: "saturate(140%) blur(6px)",
      }}
      data-testid="travel-category-nav"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-1 md:gap-2 whitespace-nowrap">
        <Link to="/travel" className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 pr-2 md:pr-3 border-r" style={{ borderColor: "var(--wofy-border)" }} data-testid="travel-nav-home">
          <MapPin className="w-3.5 h-3.5" />Travel
        </Link>
        {CATS.map(({ to, label, icon: Icon, match, stub }) => {
          const active = match.test(full);
          return (
            <Link
              key={to}
              to={to}
              className={`inline-flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-full text-xs md:text-sm transition ${
                active ? "shadow-sm" : "hover:bg-white/60"
              }`}
              style={{
                background: active ? "var(--wofy-primary)" : "transparent",
                color: active ? "#ffffff" : "inherit",
                border: active ? "1px solid transparent" : "1px solid var(--wofy-border)",
                opacity: stub ? 0.6 : 1,
              }}
              data-testid={`travel-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{label}</span>
              {stub && <span className="text-[9px] opacity-60 hidden sm:inline">soon</span>}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
